import { GraphApiCallArgs, GraphApiCallType } from './graph-api-call-args'
import asyncMethodWithRetry from '../common/async-method-with-retry'
import axios from 'axios'
import configuration from '../common/configuration'

async function setMailCustomProperties(graphAccesToken, args: any) {
  const propertySet = configuration.MANIFEST_APP_ID
  const headers = { Authorization: 'Bearer ' + graphAccesToken }

  const keyValues = args.extendedProperties
  const extendedProperties = []
  for (const key in keyValues) {
    extendedProperties.push({
      id: `String {${propertySet}} Name ${key}`,
      value: keyValues[key],
    })
  }

  return await asyncMethodWithRetry(() =>
    axios.patch(
      `/api/graph/messages/${args.id.replace(/\+/g, '_').replace(/\//g, '-')}?mailbox=${args.mailbox}`,
      {
        singleValueExtendedProperties: extendedProperties,
      },
      {
        headers: headers,
      },
    ),
  )
}

const SENT_EMAILS_MAX_PAGES = 10

async function getSentEmailsToProcess(graphAccesToken: string, args: GraphApiCallArgs) {
  const headers = {
    Authorization: 'Bearer ' + graphAccesToken,
  }
  const lastProcessedDateTime = (args.lastProcessedDateTime as string) || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
  const customProperties = `String {00020329-0000-0000-C000-000000000046} Name cecp-${configuration.MANIFEST_APP_ID}`
  const graphParam =
    `$select=Id,Subject,SentDateTime,ConversationId,ConversationIndex,InternetMessageId,sender,toRecipients,ccRecipients,bccRecipients,internetMessageHeaders&` +
    `$expand=singleValueExtendedProperties($filter=id eq 'String 0x007D' or id eq '${customProperties}'),extensions($filter=id eq 'CirrusInsight.Tracking')&` +
    `$filter=not(singleValueExtendedProperties/any(ep: ep/id eq 'String 0x007D' and contains(ep/value,'X-Is-CI-Email-Blast')))` +
    ` and not(extensions/any(e: e/id eq 'CirrusInsight.Tracking'))` +
    ` and sentDateTime ge ${lastProcessedDateTime}&` +
    `$orderby=sentDateTime asc`

  const allItems: any[] = []
  let nextLink: string | null = `/api/graph/sent?${graphParam}`
  let pageCount = 0

  while (nextLink && pageCount < SENT_EMAILS_MAX_PAGES) {
    pageCount++
    const response = await axios.get(nextLink, { headers: headers })
    const data = response.data

    if (data.value && Array.isArray(data.value)) {
      allItems.push(...data.value)
    }

    // Check if there's a next page
    if (data['@odata.nextLink']) {
      const odataNextLink = data['@odata.nextLink']
      // If it's a full URL, extract the query parameters and use with our proxy endpoint
      if (odataNextLink.startsWith('http')) {
        try {
          const url = new URL(odataNextLink)
          // Extract query parameters from the Microsoft Graph URL and use with our proxy
          const queryParams = url.search
          nextLink = `/api/graph/sent${queryParams}`
        } catch {
          // If URL parsing fails, try to extract query string manually
          const queryMatch = odataNextLink.match(/\?.*$/)
          nextLink = queryMatch ? `/api/graph/sent${queryMatch[0]}` : null
        }
      } else {
        // Already a relative URL, use as-is
        nextLink = odataNextLink
      }
    } else {
      nextLink = null
    }
  }

  // Return response with all items combined
  return { data: { value: allItems } }
}

export async function graphApiCallAsync(graphAccesToken: string, args: GraphApiCallArgs): Promise<any> {
  const headers = { Authorization: 'Bearer ' + graphAccesToken }

  switch (args.callType) {
    case GraphApiCallType.GetUserInfo:
      return await axios.get('/api/graph/me', { headers: headers })
    case GraphApiCallType.GetMessageInfo:
      return await asyncMethodWithRetry(() =>
        axios.get(`/api/graph/messages/${args.id}?mailbox=${args.mailbox}`, {
          headers: headers,
        }),
      )
    case GraphApiCallType.SetMailCustomProperties:
      return await setMailCustomProperties(graphAccesToken, args)
    case GraphApiCallType.GetSentEmailsToProcess:
      return await getSentEmailsToProcess(graphAccesToken, args)
    case GraphApiCallType.GetEventInfo:
      return await asyncMethodWithRetry(() =>
        axios.get(`/api/graph/events/${args.id}?mailbox=${args.mailbox}`, {
          headers: headers,
        }),
      )
    case GraphApiCallType.GetMessageAttachment:
      return await axios.get(`/api/graph/messages/${args.id}/attachments/${args.attachmentId}?mailbox=${args.mailbox}`, {
        headers: headers,
      })
    case GraphApiCallType.GetEventAttachment:
      return await axios.get(`/api/graph/events/${args.id}/attachments/${args.attachmentId}?mailbox=${args.mailbox}`, {
        headers: headers,
      })
    case GraphApiCallType.TranslateExchangeIds:
      return await axios.post(
        `/api/graph/translateExchangeIds?mailbox=${args.mailbox}`,
        {
          InputIds: args.inputIds,
          SourceIdType: args.sourceIdType || 'restImmutableEntryId',
          TargetIdType: args.targetIdType || 'restId',
        },
        {
          headers: headers,
        },
      )
  }
}
